// Hash router — 整個 SPA 的入口。
// 原本是 index.html 內嵌 <script type="text/babel"> 區塊，搬出來變單檔讓 esbuild 一起編譯。
// 由於 React / ReactDOM / 所有頁面元件都是 window globals，這個檔載入時其他 *.js 都已執行完。

(async function mount(){
  // ── UI 外殼選擇（SPEC §1.2「UI 外殼選擇」，plan-expressive-ui-shell.md §2.1/§2.2）──
  // 只認 index.html head script 打的 <html data-ui-shell="expressive">，router 不重讀
  // localStorage（viewport / CSS 範圍 / 外殼選擇三者一致）。一次性判定。
  const GX_ATTR = document.documentElement.getAttribute('data-ui-shell') === 'expressive';
  const GX = GX_ATTR && typeof window.ExpressiveShell === 'function' && !!window.GxModel;
  if (GX_ATTR && !GX) {
    if (window.GxCore && typeof window.GxCore.restore === 'function') window.GxCore.restore();  // revert HR/VA2/FontSizes token patches first (plan-expressive-inner-pages §3.2)
    // 降級：新版模組沒載入 → 移除 attribute 與已注入的 gx style、補回 Bento 的 1280 viewport
    document.documentElement.removeAttribute('data-ui-shell');
    document.documentElement.removeAttribute('data-gx-theme');  // Bento is always light
    document.querySelectorAll('style[id^="gx-css-"]').forEach(s => s.remove());
    if (window.screen.width < 1024) {
      const vp = document.getElementById('viewport');
      if (vp) vp.setAttribute('content', 'width=1280');
    }
    console.warn('[ui-shell] expressive modules missing → fallback to bento');
  }

  // Wait for bootstrap (auth + positions fetch)
  const start = Date.now();
  while (!window._BOOT_READY && !window._BOOT_ERROR && Date.now() - start < 30000){
    await new Promise(r => setTimeout(r, 50));
  }
  if (window._BOOT_ERROR) return;
  if (!window._BOOT_READY){
    document.getElementById('boot').textContent = '逾時，請重試';
    return;
  }

  // Override mock POSITIONS / TRADERS with real bootstrap data
  if (window._BOOT_DATA.positions.length){
    window.POSITIONS = window._BOOT_DATA.positions;
  }
  if (window._BOOT_DATA.traders.length){
    window.TRADERS = window._BOOT_DATA.traders;
  }

  /* ──────────────────  Unified hash router  ──────────────────
   *   #/  or  #/dashboard   → trading dashboard (VariantA2)
   *   #/lunch / courses / research / files → workspace tools
   *   #/employees / attendance / leave / expense / payroll / invoices /
   *   calendar / settings / audit / roles / reports / admin-tools / departments → ERP
   * Everything lives in this one shell now. hr.html is just a redirect shim.
   */
  function parseRoute() {
    const h = (window.location.hash || '').replace(/^#\/?/, '');
    const seg = h.split(/[/?]/)[0].toLowerCase();
    if (!seg || seg === 'dashboard' || seg === 'trading') return 'dashboard';
    return seg;
  }

  // Lazy getters — modules might not have finished registering at parse time.
  const ROUTE_PAGES = {
    updates:       () => window.UpdatesPage,
    // Workspace tools
    lunch:         () => window.LunchPage,
    courses:       () => window.CoursesPage,
    research:      () => window.ResearchPage,
    files:         () => window.FilesPage,
    'daily-brief': () => window.DailyBriefPage,
    'analysis-report': () => window.AnalysisReportPage,
    'morning-report': () => window.MorningReportPage,
    'morning-focus': () => window.MorningFocusPage,
    fendian:       () => window.FendianPage,
    'expert-tracker': () => window.ExpertTrackerPage,
    quantdata:     () => window.QuantDataPage,
    insider:       () => window.InsiderPage,
    'txo-board':   () => window.TxoBoardPage,
    'fund-radar':  () => window.FundRadarPage,
    bigday:        () => window.BigdayPage,
    'market-calendar': () => window.MarketCalendarPage,
    'stock-studio': () => window.StockStudioPage,
    hub:           () => window.AnalystHubPage,
    'ai-center':   () => window.AICenterPage,
    // 類別選擇頁 #/cat/<daily|quant|research|tools|erp>（hr-category-hub.jsx）——
    // 第二段由頁面自己讀 hash；權限在卡片層逐張套 canAccessRoute 等既有判定
    cat:           () => window.CategoryHubPage,
    // ERP — every former hr.html route
    employees:     () => window.EmployeesPage,
    departments:   () => window.DepartmentsPage,
    attendance:    () => window.AttendancePage,
    leave:         () => window.LeavePage,
    expense:       () => window.ExpensePage,
    payroll:       () => window.PayrollPage,
    invoices:      () => window.InvoicesPage,
    calendar:      () => window.CalendarPage,
    // 系統設定前端先註解保留（後端 routes_settings 仍可用）— 待 UI 重新設計後再啟用
    // settings:      () => window.SettingsPage,
    audit:         () => window.AuditPage,
    roles:         () => window.RolesPage,
    reports:       () => window.ReportsPage,
    'admin-tools': () => window.AdminToolsPage,
  };

  function PageShell({ route, Page }) {
    // Three-column: sidebar + main + TraderRightRail (now universal widgets).
    // Same .va2-shell / .va2-right-rail classes as VariantA2, so the
    // shared media query in <style> collapses the rail < 1480px.
    // 受限帳號（analyst / non_staff，無 privileged role）：不掛右側交易看板
    // （會打 /api/attendance/roster），改為兩欄，並讓 WorkspaceHeader 隱藏行情
    // ticker（會開 /api/market/stream SSE）——這些端點不在後端非員工閘門的
    // 白名單內，否則會持續 403 / SSE 重連。
    const restricted = window.isRestrictedAccount ? window.isRestrictedAccount(window._USER) : false;
    return (
      <div className="va2-shell va2-shell-rwd" style={{
        width: '100%',
        minHeight: '100vh',
        background: VA2.bg, color: VA2.ink,
        fontFamily: VA2.sansDisplay,
        fontFeatureSettings: '"ss01","cv11","tnum"',
        display: 'grid',
        gridTemplateColumns: restricted ? '244px minmax(0, 1fr)' : '244px minmax(0, 1fr) 360px',
      }}>
        <style>{`
          @media (max-width: 1479px) {
            .va2-shell { grid-template-columns: 244px minmax(0, 1fr) !important; }
            .va2-right-rail { display: none !important; }
          }
        `}</style>
        <TradingSidebar activeRoute={route} />
        <main style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <WorkspaceHeader hideTicker={restricted} />
          <div style={{ padding: '36px 40px 40px', flex: 1, width: '100%', minWidth: 0 }}>
            <Page />
          </div>
        </main>
        {!restricted && <TraderRightRail />}
      </div>
    );
  }

  // 新版模組（T3/T4）個別缺檔時的佔位頁（ExpressiveShell 本身缺檔已在上方降級回 Bento）
  function GxMissingPage() {
    return (
      <div role="alert" style={{ padding: 24, color: '#4A5048' }}>
        此頁面模組載入失敗，請重新整理（或在使用者選單切回舊版）。
      </div>
    );
  }

  // Expressive 外殼根元件。⚠ 必須自持 rawHash：#/ 與 #/dashboard、#/cat/daily 與
  // #/cat/quant 經 parseRoute 都是同值，App 的 setRoute 會 bail out 不重渲（plan Phase 4 #1）。
  // route/view/catKey 一律由 GxModel.gxRouteFromHash(rawHash) 推導、以 props 往下傳。
  function GxRoot({ user, cmdk }) {
    const [rawHash, setRawHash] = React.useState(window.location.hash);
    React.useEffect(() => {
      const on = () => setRawHash(window.location.hash);
      window.addEventListener('hashchange', on);
      return () => window.removeEventListener('hashchange', on);
    }, []);
    const r = window.GxModel.gxRouteFromHash(rawHash);   // {route, view, catKey, rawSeg}
    const canRoute = (key) => (typeof canAccessRoute === 'function' ? canAccessRoute(user, key) : true);
    const restrictedKeys = (typeof restrictedRouteKeys === 'function') ? restrictedRouteKeys(user) : null;
    // 'cat' 同 Bento 豁免；'me'（新版「我」頁）不在任何路由白名單，任何帳號可開
    const exempt = r.route === 'cat' || r.route === 'me';
    if (restrictedKeys && !exempt && !canRoute(r.route)) {
      window.location.hash = '#/hub';
      return null;
    }
    let Page;
    if (r.route === 'me') {
      Page = window.ExpressiveMePage || GxMissingPage;
    } else if (r.route === 'dashboard') {
      Page = (r.view === 'home' ? window.ExpressiveHome : window.GxTradePage) || GxMissingPage;
    } else {
      const getter = ROUTE_PAGES[r.route];
      const RawPage = getter && getter();
      if (!RawPage) {
        // Unknown route → bounce to home（同 Bento）
        window.location.hash = '#/';
        return null;
      }
      const allowed = r.route === 'cat' || canRoute(r.route);
      Page = allowed ? RawPage : () => <Forbidden route={r.route} user={user} />;
    }
    const GxShell = window.ExpressiveShell;
    return (
      <ToastProvider>
        <EnumProvider>
          <GxShell route={r.route} view={r.view} catKey={r.catKey} Page={Page} />
          {cmdk}
        </EnumProvider>
      </ToastProvider>
    );
  }

  function App() {
    const [route, setRoute] = React.useState(parseRoute());
    // SSE 'positions:updated' 由 VariantA2 / hr-trading 內部各自 listen 並做 in-place
    // re-render（取代 key={version} remount）— 不再在這層強制 remount，避免 tab/sort
    // 等內部 state 每次 SSE 被重置
    React.useEffect(() => {
      const onHash = () => setRoute(parseRoute());
      window.addEventListener('hashchange', onHash);
      return () => window.removeEventListener('hashchange', onHash);
    }, []);

    // Cmd+K + role-gating shared by every route
    const user = window._USER;
    // 全域浮層 —— 兩個 return 分支都會渲染這一段。
    // ⚠️ 兩分支的樹結構不同（dashboard 多包一層 <div>），所以路由切換時這裡
    // 的元件會 unmount/remount；RagChatWidget 因此把對話存 sessionStorage
    // 而非只放 state（見 hr-rag-chat.jsx 檔頭）。
    const cmdk = (
      <>
        <CommandPalette user={user} />
        {typeof RagChatWidget !== 'undefined' && <RagChatWidget user={user} />}
      </>
    );

    // Expressive 外殼（Beta）：提前 return，以下 Bento 程式碼一字不改
    if (GX) return <GxRoot user={user} cmdk={cmdk} />;

    // 受限帳號（analyst / non_staff，無 privileged role）：任何不允許的路由
    // 一律導向 #/hub 分析工作台（2026-08-24 統一，原 non_staff-only 特例導
    // #/daily-brief；'hub' 已在三份非員工路由白名單，不會迴圈）。
    // 注意：non_staff-only 開 #/ 落地仍是交易紀錄（dashboard 在其白名單、
    // 不觸發本 redirect），這裡只攔越權路由。
    // 例外：'cat' 類別選擇頁本身不是功能、無 route key（不進 ROUTES/EXTRA_ROUTES、
    // 不進白名單、不進 Cmd+K），任何帳號可開；頁內每張卡片各自過
    // canAccessRoute / canAccessToolLinks / isRestrictedAccount，受限帳號開
    // #/cat/erp 只會看到「無可用功能」空狀態，不會露出 ERP 卡片。
    const isCatHub = route === 'cat';
    const restrictedKeys = (typeof restrictedRouteKeys === 'function')
      ? restrictedRouteKeys(user) : null;
    if (restrictedKeys && !isCatHub
        && !(typeof canAccessRoute === 'function' && canAccessRoute(user, route))) {
      window.location.hash = '#/hub';
      return null;
    }

    if (route === 'dashboard') {
      return (
        <ToastProvider>
          <EnumProvider>
            <div style={{ minHeight: '100vh', background: '#EDEAE2' }}>
              <VariantA2 />
              {cmdk}
            </div>
          </EnumProvider>
        </ToastProvider>
      );
    }

    const getter = ROUTE_PAGES[route];
    const RawPage = getter && getter();
    if (!RawPage) {
      // Unknown route → bounce to dashboard
      window.location.hash = '#/';
      return null;
    }

    // Direct-URL gate — block routes the user can't access (matches former
    // hr.html ShellLayout behaviour). canAccessRoute looks at both
    // ROUTES and EXTRA_ROUTES.
    const allowed = isCatHub || (typeof canAccessRoute === 'function'
      ? canAccessRoute(user, route)
      : true);
    const Page = allowed ? RawPage : () => <Forbidden route={route} user={user} />;

    return (
      <ToastProvider>
        <EnumProvider>
          <PageShell route={route} Page={Page} />
          {cmdk}
        </EnumProvider>
      </ToastProvider>
    );
  }

  ReactDOM.createRoot(document.getElementById('root')).render(<UpdatesProvider><App /></UpdatesProvider>);
})();
